import { useState } from 'react';
import { Column, Row } from 'further-ui/components/Layout';
import { styled, Typography, CircularProgress, Tooltip } from '@mui/material';
import Switch from 'further-ui/components/Switch';
import { VehicleApplication } from '../../../types';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { DocumentCompletionStatus } from 'further-types/lp/investor-signable-document';
import Menu from 'further-ui/components/Menu';
import { MoreHoriz, SaveAlt, Delete, InfoOutline } from '@mui/icons-material';
import StatusBadge from 'components/new/StatusBadge';
import downloadPdf from 'utils/downloadPdf';
import SigningStatus from './SigningStatus';
import { getQueryKey } from '@trpc/react-query';
import { useDisclosure } from 'further-ui/hooks';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import BasicSelect from 'further-ui/components/BasicSelect';
import { AccountType } from 'further-types/investor';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type Props = {
  application: VehicleApplication;
  accountType: AccountType;
};

const SignatoriesRequiredRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(
    0,
    theme.spacingSizes.sm,
    theme.spacingSizes.xs,
    theme.spacingSizes.sm,
  ),
}));

const DocumentRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(
    theme.spacingSizes.xs,
    theme.spacingSizes.xs,
    theme.spacingSizes.xs,
    theme.spacingSizes.sm,
  ),
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

const TooltipIcon = styled(InfoOutline)(({ theme }) => ({
  fontSize: theme.fontSizes.lg,
  color: theme.palette.grey[500],
}));

const ManageApplicationDocuments: React.FC<Props> = ({
  application,
  accountType,
}) => {
  const [canEdit] = useHasPermissions([RolePermission.LpEditCommitment]);
  const voidDocumentDisclosure = useDisclosure<{ id: string }>();
  const [signatoriesRequired, setSignatoriesRequired] = useState<
    number | undefined
  >(application.signatoriesRequired ?? undefined);

  const queryKey = getQueryKey(
    api.investorSignableDocument.listAllDocumentsForApplication,
    {
      vehicleApplicationId: application.id,
      vehicleId: application.vehicleId,
    },
    'query',
  );

  const { data: availableDocuments, isLoading } =
    api.investorSignableDocument.listAllDocumentsForApplication.useQuery({
      vehicleApplicationId: application.id,
      vehicleId: application.vehicleId,
    });

  const createInvestorSignableDocument =
    api.investorSignableDocument.create.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Document added to investor onboarding.',
        invalidations: [queryKey],
      }),
    );
  const removeInvestorSignableDocument =
    api.investorSignableDocument.remove.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Document removed from investor onboarding.',
        invalidations: [queryKey],
      }),
    );

  const { mutate: downloadDocument, isPending: isDownloadingDocument } =
    api.investorSignableDocument.download.useMutation(
      useHandleTrpcMutation<{ file: string; name: string }>()({
        onSuccess: (data) => {
          downloadPdf(data.file, data.name);
        },
        invalidations: [queryKey],
      }),
    );
  const { mutate: voidExistingDocument, isPending: isVoidingDocument } =
    api.investorSignableDocument.voidExistingDocument.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Document voided and deleted.',
        invalidations: [queryKey],
        onSuccess: () => {
          voidDocumentDisclosure.onClose();
        },
      }),
    );

  const {
    mutate: updateSignatoriesRequired,
    isPending: isUpdatingSignatoriesRequired,
  } = api.vehicleApplication.updateSignatoriesRequired.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Signatories required updated.',
      invalidations: [queryKey],
    }),
  );

  const handleRowToggle = (row: {
    id: string;
    investorSignableDocument?: { id: string };
  }) => {
    if (row.investorSignableDocument) {
      removeInvestorSignableDocument.mutate({
        vehicleId: application.vehicleId,
        investorSignableDocumentId: row.investorSignableDocument.id,
      });
    } else {
      createInvestorSignableDocument.mutate({
        vehicleId: application.vehicleId,
        vehicleApplicationId: application.id,
        vehicleSigningDocumentId: row.id,
      });
    }
  };

  const toggleMutationIsPending =
    removeInvestorSignableDocument.isPending ||
    createInvestorSignableDocument.isPending;

  const toggledRowId =
    removeInvestorSignableDocument.variables?.investorSignableDocumentId ??
    createInvestorSignableDocument.variables?.vehicleSigningDocumentId;

  const handleDownloadDocument = (investorSignableDocumentId?: string) => {
    if (!investorSignableDocumentId) return;

    downloadDocument({
      vehicleId: application.vehicleId,
      investorSignableDocumentId,
    });
  };

  const handleVoidAndDeleteDocument = (investorSignableDocumentId: string) => {
    if (!investorSignableDocumentId) return;

    voidExistingDocument({
      vehicleId: application.vehicleId,
      investorSignableDocumentId,
    });
  };

  if (isLoading) {
    return (
      <Column spacing="lg" centered>
        <CircularProgress size={16} />
      </Column>
    );
  }

  const isNotUntoggleable = (row: {
    investorSignableDocument?: { documentStatus: DocumentCompletionStatus };
  }) =>
    !!row.investorSignableDocument &&
    row.investorSignableDocument.documentStatus !==
      DocumentCompletionStatus.CreatedNotStarted;

  return (
    <Column>
      {accountType === AccountType.LegalEntity ? (
        <SignatoriesRequiredRow justify="space-between" centered>
          <Column>
            <Row centered spacing="xs">
              <Typography variant="h6">Signatories required</Typography>{' '}
              <Tooltip
                title="Select the number of signatories this investor requires to sign validly. If you are uncertain, leave this blank and the investor will be asked to confirm. Once documents are sent for signing, changing the number of signatories will only affect existing documents if edited down, not up."
                arrow
              >
                <TooltipIcon />
              </Tooltip>
            </Row>
          </Column>
          <Column>
            <BasicSelect
              fullWidth
              size="small"
              placeholder="-"
              value={signatoriesRequired}
              onChange={(value: number | undefined) => {
                setSignatoriesRequired(value);
                updateSignatoriesRequired({
                  vehicleId: application.vehicleId,
                  vehicleApplicationId: application.id,
                  signatoriesRequired: value,
                });
              }}
              disabled={isUpdatingSignatoriesRequired || !canEdit}
              nullOptionLabel="-"
              options={[
                { value: 1, label: '1' },
                { value: 2, label: '2' },
                { value: 3, label: '3' },
                { value: 4, label: '4' },
              ]}
            />
          </Column>
        </SignatoriesRequiredRow>
      ) : null}
      {availableDocuments?.map((row) => (
        <DocumentRow key={row.id} justify="space-between" centered>
          <Column>
            <Row centered spacing="xs">
              {row.investorSignableDocument ? (
                <StatusBadge
                  color={
                    row.investorSignableDocument.documentStatus ===
                    DocumentCompletionStatus.Completed
                      ? 'success'
                      : 'error'
                  }
                />
              ) : null}
              <Typography variant="h6">{row.documentName}</Typography>
              <SigningStatus
                signatories={row.investorSignableDocument?.signatories}
                signatoriesRequired={
                  signatoriesRequired ?? application.signatoriesRequired
                }
              />
            </Row>
          </Column>
          <Column>
            <Row centered spacing="xs">
              {toggleMutationIsPending &&
              [row.id, row.investorSignableDocument?.id].includes(
                toggledRowId,
              ) ? (
                <CircularProgress size={16} />
              ) : null}
              <Switch
                disabled={
                  toggleMutationIsPending || isNotUntoggleable(row) || !canEdit
                }
                checked={!!row.investorSignableDocument}
                onChange={() => {
                  handleRowToggle(row);
                }}
              />
              {!!row.investorSignableDocument && (
                <Menu
                  handleSize="small"
                  handleIcon={MoreHoriz}
                  items={[
                    {
                      label: 'Download',
                      icon: SaveAlt,
                      onClick: () => {
                        handleDownloadDocument(
                          row.investorSignableDocument?.id,
                        );
                      },
                      disabled: isDownloadingDocument,
                    },
                    ...(isNotUntoggleable(row)
                      ? [
                          {
                            label: 'Void and delete',
                            icon: Delete,
                            hidden: !canEdit,
                            onClick: () => {
                              voidDocumentDisclosure.stageAction({
                                id: row.investorSignableDocument?.id ?? '',
                              });
                            },
                          },
                        ]
                      : []),
                  ]}
                />
              )}
            </Row>
          </Column>
        </DocumentRow>
      ))}
      <ConfirmDialog
        open={voidDocumentDisclosure.isOpen}
        onClose={voidDocumentDisclosure.onClose}
        title="Void and delete"
        maxWidth="sm"
        content="Do you want to void and delete this document? This will remove the investor’s ability to sign and view the document and the investor will be notified."
        loading={isVoidingDocument}
        actions={{
          confirm: {
            label: 'Void and delete',
            color: 'error',
            onClick: () => {
              handleVoidAndDeleteDocument(
                voidDocumentDisclosure.actionProps.id,
              );
            },
          },
        }}
      />
    </Column>
  );
};

export default ManageApplicationDocuments;
